import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'ttag';
import config from '../../../../config/channels';
import { operatorName } from '../../../../config/operator';
import { TDeliveryVenue } from '../../../../contexts/delivery-venue/delivery-venue-context';
import useOperatorContext from '../../../../contexts/operator/useOperatorContext';
import { filterDeliveryVenues } from '../../../../services/api/delivery-venue';
import { CTA } from '../../../general/Button';
import LocationPicker, { PickerLocation } from './LocationPicker';
import ModalPanel from './ModalPanel';
import { trackSiteSelectChoose, trackSiteSelectComplete } from '../../../../services/siteSelectTracking';

type VenuesPanelProps = {
    venues: TDeliveryVenue[];
    focusRequest?: number;
};

const VenuesPanel = ({ venues, focusRequest }: VenuesPanelProps) => {
    const navigate = useNavigate();
    const operatorContext = useOperatorContext();

    const canSearch = operatorContext.operator?.theme.v4.location_search ?? false;

    const [query, setQuery] = useState('');
    // venues matching what was typed
    const [found, setFound] = useState<{ uid: string }[] | null>(null);
    const [selected, setSelected] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const search = query.trim();

        if (search.length < 3) {
            return;
        }

        // wait for typing to pause, and ignore an answer that comes after a newer question
        let current = true;

        const timer = setTimeout(() => {
            filterDeliveryVenues(operatorName, search)
                .then((data: { uid: string }[]) => current && setFound(data))
                .catch(() => current && setFound(null));
        }, 250);

        return () => {
            current = false;
            clearTimeout(timer);
        };
    }, [query]);

    const shown = query.trim().length > 2 && found
        ? venues.filter((venue) => found.some((match) => match.uid === venue.uid))
        : venues;

    const locations: PickerLocation[] = shown.map((venue) => {
        const open = venue.channels.venues?.is_open ?? false;

        return {
            id: venue.uid,
            name: venue.name,
            address: `${venue.address_1}, ${venue.address_city} ${venue.address_postcode}`,
            open,
            status: open ? t`Currently open` : t`Not currently accepting orders`,
        };
    });

    const start = () => {
        const venue = venues.find((venue) => venue.uid === selected);

        if (!venue) {
            return;
        }

        localStorage.setItem('address', JSON.stringify({
            deliveryVenueName: venue.name,
            address1: venue.address_1,
            address2: venue.address_2,
            city: venue.address_city,
            postcode: venue.address_postcode,
        }));
        localStorage.setItem('postcode', venue.address_postcode);
        localStorage.setItem('delivery-venue', venue.uid);

        // a closed venue can still be looked at
        const open = venue.channels.venues?.is_open ?? false;

        setLoading(true);
        trackSiteSelectComplete('modal', 'venues', venue.site.handle);
        navigate(`/${config.venues.handle}/${venue.site.handle}${open ? '' : '?viewOnly=1'}`);
    };

    return (
        <ModalPanel
            focusRequest={focusRequest}
            footer={
                <CTA block disabled={!selected || loading} loading={loading} onClick={start}>
                    <span>{t`View menu`}</span>
                </CTA>
            }
        >
            <LocationPicker
                question={t`Which venue are you in?`}
                label={t`Venues`}
                noun={{ one: t`venue`, many: t`venues` }}
                locations={locations}
                total={venues.length}
                selected={selected}
                onSelect={(id) => {
                    setSelected(id);
                    const venue = venues.find((venue) => venue.uid === id);
                    if (venue) trackSiteSelectChoose('modal', 'venues', venue.site.handle);
                }}
                query={canSearch ? query : undefined}
                onQuery={canSearch ? setQuery : undefined}
                placeholder={t`Venue, town or postcode`}
            />
        </ModalPanel>
    );
};

export default VenuesPanel;
